import React, { useEffect, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, Columns2, History, Pencil, RefreshCw, Trash2, X } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import type { AnalysisRun, AnalyzerStage } from "../../api/types";
import { parseUtcDate } from "../../utils";
import {
  compareSideColor,
  compareSideInk,
  type CompareSide,
} from "../processes/processCompareModel";
import { HelpHint } from "../processes/processSidebarUi";
import { ANALYZER_STAGES, stageIndex } from "./analyzerStages";
import {
  RunDialogActions,
  RunDialogShell,
  dangerButtonClass,
  primaryButtonClass,
  useRunAction,
} from "./runDialog";

/**
 * The stages a re-run may start at. Cleanup is left out because a re-run that
 * redoes it reuses nothing, which is the menu's plain "Re-run analysis"; the
 * adapter is left out because it is not a stage anyone re-runs on its own.
 * Summarize is offered so a run from before that stage existed can gain a
 * session summary with one model call, reusing all of its lifts.
 */
const RESUMABLE_STAGES: AnalyzerStage[] = [
  "annotator",
  "lift-to-l1",
  "lift-to-l2",
  "lift-to-l3",
  "lift-to-l4",
  "summarize",
];

/** How a re-run starts: a full run, or resuming a run at one of its stages. */
export type RerunOptions = {
  startStage: AnalyzerStage;
  baseRunId: string;
  disableTools?: boolean;
};

/**
 * Whether `run` can feed a re-run that starts at `stage`: it must hold the
 * output of every stage before it. Stricter than the pipeline's real
 * dependencies, and it needs no knowledge of them — the same rule the server
 * validates with.
 */
const canResumeAt = (run: AnalysisRun, stage: AnalyzerStage) =>
  ANALYZER_STAGES.slice(0, stageIndex(stage)).every(earlier =>
    run.available_stages.includes(earlier.id),
  );

/**
 * The earliest stage `run` can feed, or `undefined` if it can feed none — which
 * is what makes its re-run action unofferable. Asked in both places rather than
 * approximated by "has any artifacts at all": a run holding, say, only the
 * adapter's `analysis.json` has artifacts and still nothing to resume from.
 */
const firstResumableStage = (run: AnalysisRun): AnalyzerStage | undefined =>
  RESUMABLE_STAGES.find(stage => canResumeAt(run, stage));

const runHasResult = (run: AnalysisRun) => run.status === "finished" && !run.error;

type AnalysisRunSelectorProps = {
  runs: AnalysisRun[];
  /** The run shown when none is picked: the newest completed one. */
  defaultRunId: string | null;
  /** The run currently on screen, or `null` while showing the default. */
  selectedRunId: string | null;
  /** Where the re-run dialog starts unless the operator picks otherwise. */
  defaultRerunStage: AnalyzerStage;
  /** The run stacked under the shown one, or `null` when not comparing. */
  compareRunId: string | null;
  /**
   * Which half of a comparison this picker belongs to, or `undefined` for the
   * page header's own. A band's picker re-aims that band alone, so either run
   * is re-chosen from the bar that names it; the header's is the way in and out
   * of the comparison itself.
   */
  side?: CompareSide;
  onSelect: (runId: string | null) => void;
  /** Ends the comparison with `null`, or stacks one run under the shown one. */
  onCompare: (runId: string | null) => void;
  /** Starts a comparison of two runs picked together, top band first. */
  onComparePair: (aRunId: string, bRunId: string) => void;
  onRerun: (options?: RerunOptions) => Promise<void>;
  onRename: (runId: string, name: string | null) => Promise<void>;
  onDelete: (runId: string) => Promise<void>;
  rerunBusy?: boolean;
};

/**
 * A run's own name, or the moment it ran — its identity until someone names it.
 *
 * `created_at` is UTC without a zone suffix, which `new Date` would read as a
 * local wall clock: parsed that way the list is silently off by the reader's
 * offset, which on a history of re-runs minutes apart is not a discrepancy
 * anyone would spot.
 */
export const runLabel = (run: AnalysisRun) =>
  run.name || format(parseUtcDate(run.created_at), "MMM d, HH:mm");

/**
 * The zone these times are rendered in, as the platform abbreviates it ("CEST",
 * "GMT+2"). Named once above the list because a bare "09:38" is only a fact if
 * the reader knows which clock it is on — and the timestamps come from a server
 * that is on a different one.
 */
const localZoneLabel = (): string =>
  new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(new Date())
    .find(part => part.type === "timeZoneName")?.value ?? "";

/** The buttons that sit in the menu's title row, opposite the heading. */
const menuHeaderButtonClass =
  "inline-flex shrink-0 items-center gap-1 rounded-full border border-outline bg-surface px-2 py-0.5 text-[11px] font-medium text-text-mid transition hover:bg-muted-surface hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30";

/** The A/B letter a run carries while the two-run pick is being made. */
const PickMark = ({ side }: { side: CompareSide }) => (
  <span
    aria-hidden="true"
    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] text-[10px] font-bold leading-none text-white"
    style={{ backgroundColor: compareSideInk(side) }}
  >
    {side === "a" ? "A" : "B"}
  </span>
);

/**
 * Picks which of a recording's analysis runs the page shows, and carries the
 * actions that make sense on one: re-running, labelling, and dropping a run.
 *
 * Only rendered for users who may re-analyze — everyone else sees the newest
 * completed run and no history at all.
 */
const AnalysisRunSelector = ({
  runs,
  defaultRunId,
  selectedRunId,
  compareRunId,
  side,
  defaultRerunStage,
  onSelect,
  onCompare,
  onComparePair,
  onRerun,
  onRename,
  onDelete,
  rerunBusy = false,
}: AnalysisRunSelectorProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState<AnalysisRun | null>(null);
  const [deleting, setDeleting] = useState<AnalysisRun | null>(null);
  const [rerunningFrom, setRerunningFrom] = useState<AnalysisRun | null>(null);
  /**
   * The runs picked so far towards a comparison, or `null` when the menu is not
   * asking for a pair. Two runs are chosen together rather than one being
   * stacked under whatever happened to be on screen: a comparison is a
   * statement about a pair, and picking it as a pair is what makes the A and B
   * bands the reader's own choice.
   */
  const [picks, setPicks] = useState<string[] | null>(null);

  /** A row action opens a dialog, and the menu it was picked from is done. */
  const openDialog = (open: (run: AnalysisRun) => void) => (run: AnalysisRun) => {
    setMenuOpen(false);
    open(run);
  };

  if (runs.length === 0) {
    return null;
  }

  const shownRunId = selectedRunId ?? defaultRunId;
  const completedRuns = runs.filter(runHasResult);

  // While a comparison is on, the header's picker stands down to the way out of
  // it: each band carries its own picker, so the run a band shows is re-aimed
  // in the bar that names it rather than from up here, where nothing said which
  // of the two a change would land on.
  if (!side && compareRunId) {
    return (
      <button
        type="button"
        onClick={() => onCompare(null)}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-outline bg-surface px-3 py-1 text-xs font-medium text-text-mid shadow-sm transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
        {t("exit_compare")}
      </button>
    );
  }

  const zoneLabel = localZoneLabel();
  /** The run this picker points at: its own band's, or the page's shown run. */
  const pickedRunId = side === "b" ? compareRunId : shownRunId;
  const pickedRun = runs.find(run => run.id === pickedRunId);
  /** The run the other band is on, which this one therefore cannot also take. */
  const otherBandRunId = side ? (side === "b" ? shownRunId : compareRunId) : null;

  const closeMenu = () => {
    setMenuOpen(false);
    setPicks(null);
  };

  /** Point this picker's band — or the page — at one run. */
  const selectRun = (runId: string) => {
    closeMenu();
    if (side === "b") {
      onCompare(runId);
    } else {
      // The default run is shown with no `run` in the URL at all.
      onSelect(runId === defaultRunId ? null : runId);
    }
  };

  /** A pick towards the pair; the second one starts the comparison. */
  const togglePick = (runId: string) => {
    const current = picks ?? [];
    if (current.includes(runId)) {
      setPicks(current.filter(id => id !== runId));
      return;
    }
    const next = [...current, runId];
    if (next.length < 2) {
      setPicks(next);
      return;
    }
    closeMenu();
    onComparePair(next[0], next[1]);
  };

  return (
    <>
      <DropdownMenu.Root
        open={menuOpen}
        onOpenChange={open => {
          setMenuOpen(open);
          // A menu that closes half way through a pair abandons the pick, so it
          // does not reopen holding a choice the reader has forgotten making.
          if (!open) {
            setPicks(null);
          }
        }}
      >
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30",
              side
                ? "text-text hover:brightness-[0.97]"
                : "border-outline bg-surface text-text-mid hover:bg-muted-surface",
            )}
            // A band's picker wears that band's colour, so the pill and the bar
            // it sits in read as one control rather than two.
            style={
              side
                ? {
                    backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 16%, var(--color-surface))`,
                    borderColor: `color-mix(in srgb, ${compareSideColor(side)} 45%, transparent)`,
                  }
                : undefined
            }
          >
            <History className="h-3.5 w-3.5 text-text-dim" aria-hidden="true" />
            {/* What the pill picks, said out loud: a bare date beside the
                session's own dates reads as one more of them, not as a
                control. The band pills say "change run" already. */}
            {!side && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-text-dim">
                {t("analysis_run_pill_label")}:
              </span>
            )}
            <span className="max-w-[16rem] truncate">
              {side ? t("change_run") : pickedRun ? runLabel(pickedRun) : t("analysis_runs")}
            </span>
            {!side && pickedRun && pickedRun.id === defaultRunId && (
              <span className="text-text-dim">({t("analysis_run_latest")})</span>
            )}
            {/* The whole menu is an operator's tool, so the badge sits on the
                pill rather than on the one item inside that carried it. */}
            <span className="rounded-full border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-strong">
              Admin
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-text-dim" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className="z-50 min-w-[20rem] rounded-xl border border-outline bg-surface p-1 shadow-lg"
          >
            <div className="flex items-center justify-between gap-2 px-2 py-1.5">
              <p className="min-w-0 text-[10px] font-semibold uppercase tracking-wide text-text-dim">
                {picks ? (
                  t("compare_pick_two_runs")
                ) : (
                  <>
                    {t("analysis_runs")}
                    {zoneLabel && (
                      <span className="ml-1 normal-case text-text-dim/80">· {zoneLabel}</span>
                    )}
                  </>
                )}
              </p>
              {/* The way into a comparison, and back out of the pick. Only the
                  page's own picker offers it: a band's picker is already inside
                  a comparison, which the header's Exit leaves. */}
              {/* A pair needs two runs that kept a result; with fewer, there is
                  nothing to offer a comparison of. */}
              {!side &&
                completedRuns.length >= 2 &&
                (picks ? (
                  <button
                    type="button"
                    onClick={() => setPicks(null)}
                    className={menuHeaderButtonClass}
                  >
                    {t("cancel")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPicks([])}
                    className={menuHeaderButtonClass}
                  >
                    <Columns2 className="h-3 w-3" aria-hidden="true" />
                    {t("compare")}
                  </button>
                ))}
            </div>
            {runs.map(run => {
              const isShown = run.id === pickedRunId;
              const pickIndex = picks ? picks.indexOf(run.id) : -1;
              const cancelled = run.status === "cancelled";
              // A run without a result has nothing to display — it stays in
              // the list to be read about and deleted. A band cannot take the
              // run the other band is already on either.
              const selectable = runHasResult(run) && (picks !== null || run.id !== otherBandRunId);
              return (
                <div
                  key={run.id}
                  className={clsx(
                    "group/run flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition",
                    isShown && !picks ? "bg-muted-surface" : "hover:bg-muted-surface",
                  )}
                >
                  <button
                    type="button"
                    disabled={!selectable}
                    onClick={() => {
                      if (!selectable) return;
                      if (picks) {
                        togglePick(run.id);
                      } else {
                        selectRun(run.id);
                      }
                    }}
                    title={
                      !runHasResult(run)
                        ? cancelled
                          ? t("analysis_cancelled_page_message")
                          : t("compare_with_run_unavailable")
                        : run.id === otherBandRunId
                          ? t("compare_run_on_other_band")
                          : undefined
                    }
                    className={clsx(
                      "flex min-w-0 flex-1 items-center gap-2 text-left",
                      selectable ? "cursor-pointer text-text" : "cursor-default text-text-dim",
                    )}
                  >
                    {picks ? (
                      pickIndex >= 0 ? (
                        <PickMark side={pickIndex === 0 ? "a" : "b"} />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="h-4 w-4 shrink-0 rounded-[4px] border border-outline"
                        />
                      )
                    ) : (
                      <Check
                        className={clsx("h-4 w-4 shrink-0", isShown ? "opacity-100" : "opacity-0")}
                        aria-hidden="true"
                      />
                    )}
                    <span className="min-w-0 truncate">{runLabel(run)}</span>
                    {cancelled && (
                      <span className="shrink-0 text-xs text-text-dim">
                        ({t("cancelled").toLowerCase()})
                      </span>
                    )}
                    {run.id === defaultRunId && (
                      <span className="shrink-0 text-xs text-text-dim">
                        ({t("analysis_run_latest")})
                      </span>
                    )}
                    {run.error && (
                      <span
                        title={run.error}
                        className="shrink-0 rounded-full border border-danger/40 bg-danger/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-danger-strong"
                      >
                        {t("failed")}
                      </span>
                    )}
                  </button>
                  {/* A run's own actions are out of the way while a pair is
                      being picked: every row in that mode means one thing. */}
                  {!picks && (
                    <>
                      <button
                        type="button"
                        disabled={!firstResumableStage(run)}
                        onClick={() => openDialog(setRerunningFrom)(run)}
                        title={
                          firstResumableStage(run)
                            ? t("rerun_from_run")
                            : t("rerun_from_run_unavailable")
                        }
                        aria-label={t("rerun_from_run")}
                        // Stays visible when disabled, unlike the other row
                        // actions: a run with no artifacts is exactly the case
                        // whose tooltip is worth reading.
                        className="shrink-0 rounded p-1 text-text-dim opacity-0 transition hover:bg-surface hover:text-text focus-visible:opacity-100 group-hover/run:opacity-100 disabled:cursor-not-allowed disabled:text-text-dim/40 disabled:hover:bg-transparent"
                      >
                        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openDialog(setRenaming)(run)}
                        title={t("rename_analysis_run")}
                        aria-label={t("rename_analysis_run")}
                        className="shrink-0 rounded p-1 text-text-dim opacity-0 transition hover:bg-surface hover:text-text focus-visible:opacity-100 group-hover/run:opacity-100"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openDialog(setDeleting)(run)}
                        title={t("delete_analysis_run")}
                        aria-label={t("delete_analysis_run")}
                        className="shrink-0 rounded p-1 text-text-dim opacity-0 transition hover:bg-danger/10 hover:text-danger focus-visible:opacity-100 group-hover/run:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            {!picks && (
              <>
                <DropdownMenu.Separator className="my-1 h-px bg-outline" />
                <DropdownMenu.Item
                  disabled={rerunBusy}
                  // The pill in the header carries the failure; swallowed here so a
                  // rejection the caller re-throws for the dialogs does not go
                  // unhandled on the one path that has no dialog to catch it.
                  onSelect={() => void onRerun().catch(() => {})}
                  className="flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm text-text outline-none transition hover:bg-muted-surface focus:bg-muted-surface data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60"
                >
                  <RefreshCw className="h-4 w-4 text-text-dim" aria-hidden="true" />
                  <span className="flex-1">{t("rerun_analysis")}</span>
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {renaming && (
        <RunRenameDialog
          run={renaming}
          onCancel={() => setRenaming(null)}
          onConfirm={async name => {
            await onRename(renaming.id, name);
            setRenaming(null);
          }}
        />
      )}
      {rerunningFrom && (
        <RunRerunDialog
          run={rerunningFrom}
          defaultStage={defaultRerunStage}
          onCancel={() => setRerunningFrom(null)}
          onConfirm={async options => {
            await onRerun(options);
            setRerunningFrom(null);
          }}
        />
      )}
      {deleting && (
        <RunDeleteDialog
          run={deleting}
          // Deleting the last completed run leaves the session with nothing to
          // show, which is worth saying out loud before it happens.
          lastCompleted={runHasResult(deleting) && completedRuns.length === 1}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await onDelete(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </>
  );
};

type RunRenameDialogProps = {
  run: AnalysisRun;
  onCancel: () => void;
  onConfirm: (name: string | null) => Promise<void>;
};

const RunRenameDialog = ({ run, onCancel, onConfirm }: RunRenameDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(run.name ?? "");
  const { busy, error, run: submitting } = useRunAction(t("analysis_run_rename_failed"));

  useEffect(() => {
    setName(run.name ?? "");
  }, [run]);

  // An emptied field clears the label, putting the run back to its date.
  const submit = () => submitting(() => onConfirm(name.trim() || null));

  return (
    <RunDialogShell
      labelledBy="analysis-run-rename-title"
      dismissLabel={t("cancel")}
      onDismiss={onCancel}
    >
      <h2 id="analysis-run-rename-title" className="text-lg font-semibold">
        {t("rename_analysis_run")}
      </h2>
      <p className="mt-2 text-sm leading-6 text-text-mid">{t("analysis_run_rename_message")}</p>
      <input
        autoFocus
        value={name}
        onChange={event => setName(event.target.value)}
        onKeyDown={event => {
          if (event.key === "Enter") {
            void submit();
          }
        }}
        placeholder={t("analysis_run_name_placeholder")}
        className="mt-4 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-text outline-none transition focus:border-info/50"
      />
      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
      <RunDialogActions
        busy={busy}
        confirmLabel={t("save")}
        confirmClassName={primaryButtonClass}
        onCancel={onCancel}
        onConfirm={() => void submit()}
      />
    </RunDialogShell>
  );
};

type RunDeleteDialogProps = {
  run: AnalysisRun;
  lastCompleted: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

const RunDeleteDialog = ({ run, lastCompleted, onCancel, onConfirm }: RunDeleteDialogProps) => {
  const { t } = useTranslation();
  const { busy, error, run: submitting } = useRunAction(t("analysis_run_delete_failed"));

  return (
    <RunDialogShell
      labelledBy="analysis-run-delete-title"
      dismissLabel={t("cancel")}
      onDismiss={onCancel}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger">
          <Trash2 size={20} />
        </div>
        <div className="min-w-0">
          <h2 id="analysis-run-delete-title" className="text-lg font-semibold">
            {t("delete_analysis_run")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-text-mid">
            {lastCompleted
              ? t("delete_last_analysis_run_message")
              : t("delete_analysis_run_message")}
          </p>
          <p className="mt-2 truncate text-sm font-medium text-text">{runLabel(run)}</p>
        </div>
      </div>
      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
      <RunDialogActions
        busy={busy}
        confirmLabel={t("delete_analysis_run")}
        confirmClassName={dangerButtonClass}
        onCancel={onCancel}
        onConfirm={() => void submitting(onConfirm)}
      />
    </RunDialogShell>
  );
};

type RunRerunDialogProps = {
  run: AnalysisRun;
  defaultStage: AnalyzerStage;
  onCancel: () => void;
  onConfirm: (options?: RerunOptions) => Promise<void>;
};

/**
 * Starts a re-run off one earlier run: which of its stages to keep, and where
 * the pipeline picks up. The stages before the cut are copied into the new run,
 * so the run this one is based on can later be renamed or deleted without
 * taking the new one's artifacts with it.
 */
const RunRerunDialog = ({ run, defaultStage, onCancel, onConfirm }: RunRerunDialogProps) => {
  const { t } = useTranslation();
  const { busy, error, run: submitting } = useRunAction(t("analysis_rerun_failed"));
  // A run that cannot feed the default stage opens on the earliest stage it can
  // feed, rather than on a choice the server would reject.
  const [stage, setStage] = useState<AnalyzerStage>(() =>
    RESUMABLE_STAGES.includes(defaultStage) && canResumeAt(run, defaultStage)
      ? defaultStage
      : // Non-null: the dialog is only reachable from a run that can feed one.
        (firstResumableStage(run) as AnalyzerStage),
  );
  const [disableTools, setDisableTools] = useState(false);

  return (
    <RunDialogShell
      labelledBy="analysis-run-rerun-title"
      dismissLabel={t("cancel")}
      onDismiss={onCancel}
    >
      <div className="flex items-start gap-1.5">
        <h2 id="analysis-run-rerun-title" className="min-w-0 text-lg font-semibold">
          {t("rerun_from_run_named", { name: runLabel(run) })}
        </h2>
        {/* A box as tall as the title's first line, which a long run name wraps
            past: the "?" centres on that line instead of sitting on its baseline. */}
        <span className="flex h-7 shrink-0 items-center">
          <HelpHint
            standalone
            side="bottom"
            align="start"
            label={t("rerun_from_run")}
            text={t("rerun_from_run_message")}
            // Above the dialog this "?" sits in, which the tooltip portals out of.
            contentClassName="z-[120]"
          />
        </span>
      </div>

      <label
        htmlFor="analysis-run-rerun-stage"
        className="mt-4 block text-xs font-semibold uppercase tracking-wide text-text-dim"
      >
        {t("start_at_stage")}
      </label>
      <select
        id="analysis-run-rerun-stage"
        value={stage}
        onChange={event => setStage(event.target.value as AnalyzerStage)}
        className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-text outline-none transition focus:border-info/50"
      >
        {ANALYZER_STAGES.filter(known => RESUMABLE_STAGES.includes(known.id)).map(known => (
          <option key={known.id} value={known.id} disabled={!canResumeAt(run, known.id)}>
            {known.display}
          </option>
        ))}
      </select>
      <button
        type="button"
        role="switch"
        aria-checked={!disableTools}
        onClick={() => setDisableTools(disabled => !disabled)}
        className="mt-3 inline-flex items-center gap-2 rounded-full border border-outline bg-surface px-2.5 py-1 text-xs font-medium text-text-mid transition hover:bg-muted-surface"
      >
        <span>{t("use_lift_tools")}</span>
        <span
          className={`inline-flex h-3.5 w-6 items-center rounded-full transition ${
            disableTools ? "bg-outline" : "bg-primary"
          }`}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full bg-surface shadow transition ${
              disableTools ? "translate-x-0.5" : "translate-x-3"
            }`}
          />
        </span>
      </button>

      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
      <RunDialogActions
        busy={busy}
        confirmLabel={t("rerun_analysis")}
        confirmClassName={primaryButtonClass}
        onCancel={onCancel}
        onConfirm={() =>
          void submitting(() => onConfirm({ startStage: stage, baseRunId: run.id, disableTools }))
        }
      />
    </RunDialogShell>
  );
};

export default AnalysisRunSelector;
